import { getAreSimilarColors } from '../../../common/colors/colorComparisonUtil';
import { getIsLightColor } from '../../../common/colors/coreColorUtil';
import { getElementColorHexValue } from '../../../common/colors/elementColorFormatUtil';
import { getBackgroundColor } from '../../../common/elements/utils/elementPropertyUtils';
import {
    BACKGROUND_COLORS,
    ELEMENT_DEFAULT_BACKGROUND_DARK,
    ELEMENT_DEFAULT_BACKGROUND_LIGHT,
    LABEL_COLORS,
    MENU_BACKGROUND_DARK,
    MENU_BACKGROUND_LIGHT,
} from '../../../common/colors/colorConstants';
import type { ImMNElement } from '../../../common/elements/elementModelTypes';
import type { Label } from '../../../common/labels/labelTypes';

type LabelOutlineProperties = {
    hasSimilarColorToBackground: boolean;
    outlineColor: string;
};

const DEFAULT_FALSE_RESPONSE = {
    hasSimilarColorToBackground: false,
    outlineColor: '',
};

const getLabelColorHex = (label: Label): string | null => {
    if (!label.color) return null;

    return getElementColorHexValue(label.color, LABEL_COLORS) ?? LABEL_COLORS.WHITE.hex;
};

const getOutlineColorForLabel = (labelColorHex: string): string =>
    getIsLightColor(labelColorHex) ? 'outline-quinary_alt' : 'outline-quinary';

const getLabelOutlinePropertiesForBackground = (
    label: Label,
    backgroundHex: string,
    matchingBackgroundOutlineColor: string,
): LabelOutlineProperties => {
    const labelColorHex = getLabelColorHex(label);
    if (!labelColorHex) return DEFAULT_FALSE_RESPONSE;

    const hasSimilarColorToBackground = getAreSimilarColors(labelColorHex, backgroundHex);
    if (!hasSimilarColorToBackground) return DEFAULT_FALSE_RESPONSE;

    return { hasSimilarColorToBackground, outlineColor: matchingBackgroundOutlineColor };
};

export const getLabelOutlinePropertiesForElement = (label: Label, element: ImMNElement, isDarkModeTheme: boolean) => {
    const defaultBackground = isDarkModeTheme ? ELEMENT_DEFAULT_BACKGROUND_DARK : ELEMENT_DEFAULT_BACKGROUND_LIGHT;
    const labelColorHex = getLabelColorHex(label);
    const elementColor = getBackgroundColor(element);

    if (!labelColorHex) return DEFAULT_FALSE_RESPONSE;

    if (!elementColor && getAreSimilarColors(labelColorHex, defaultBackground)) {
        return {
            hasSimilarColorToBackground: true,
            outlineColor: 'outline-label-contrast-border',
        };
    }

    const elementColorHex = getElementColorHexValue(elementColor, BACKGROUND_COLORS) ?? defaultBackground;
    const hasSimilarColorToBackground = getAreSimilarColors(labelColorHex, elementColorHex);

    return {
        hasSimilarColorToBackground,
        outlineColor: hasSimilarColorToBackground ? getOutlineColorForLabel(labelColorHex) : '',
    };
};

export const getLabelOutlinePropertiesForMenu = (label: Label, isDarkModeTheme: boolean) => {
    const menuBackground = isDarkModeTheme ? MENU_BACKGROUND_DARK : MENU_BACKGROUND_LIGHT;
    return getLabelOutlinePropertiesForBackground(label, menuBackground, 'outline-label-contrast-border');
};
